import {
  Box,
  Button,
  Typography,
  useTheme,
  Chip,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import { Close, ArrowBack } from "@mui/icons-material";

import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";

// Importing the action to fetch all public plans and create subscription
import { AppDispatch, RootState } from "../stores";
import { getCustomerUserAssignAndPublicPlan } from "../features/plan-management-module/planAction";

// Stripe imports
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { createSubscriptionAction } from "../features/subscription-module/subscriptionAction";
import { getAuthenticatedUser } from "../features/auth-module/authAction";
import { logout } from "../features/auth-module/authSlice";

// Load Stripe with public key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

const StyledBackButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: "#f2efe9",
  color: "#455354",
  transition: "background 0.24s ease-in-out, color 0.24s ease-in-out",
  "&:hover": {
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    color: "#f2efe9",
  },
}));

const PlansContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(200px, 1fr))",
  gap: theme.spacing(3),
  padding: theme.spacing(4),
  justifyContent: "center",
  alignItems: "start",
  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "repeat(2, minmax(250px, 1fr))",
    padding: theme.spacing(3),
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
}));

const PlanCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: 8,
  padding: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  textAlign: "center",
  transition: "transform 0.24s, background 0.24s",
  position: "relative",
  minHeight: "400px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  "&:hover": {
    transform: "scale(1.01)",
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    "& .description-text": {
      color: "#f2efe9",
    },
    "& .feature-item": {
      color: "#f2efe9",
    },
    "& .buy-button": {
      backgroundColor: "#f2efe9",
      color: "#455354",
    },
  },
}));

const PlanTittleCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#f2efe9",
  borderRadius: 8,
  padding: theme.spacing(1),
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  textAlign: "left",
  transition: "transform 0.24s",
  position: "relative",
  [theme.breakpoints.down("md")]: {
    maxWidth: "100%",
    marginBottom: theme.spacing(2),
  },
}));

const PlanPrice = styled(Typography)(({ theme }) => ({
  fontSize: 25,
  fontWeight: 600,
  color: "#455354",
  marginBottom: theme.spacing(1),
  letterSpacing: "-0.02em",
}));

const FeaturesList = styled(Box)(({ theme }) => ({
  textAlign: "left",
  padding: theme.spacing(2),
  flexGrow: 1,
}));

const FeatureItem = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  fontSize: 11,
  color: "#3f3f3f",
  marginBottom: theme.spacing(1),
  "&::before": {
    content: '"✓"',
    color: "#4caf50",
    fontWeight: 600,
    fontSize: 17,
    marginRight: theme.spacing(1.1),
    marginTop: 1,
  },
}));

const CustomChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(3.2),
  right: theme.spacing(1.2),
  background: "linear-gradient(to bottom, #8ec5aa, #5c8e78)",
  color: "#fff",
  fontWeight: 600,
  borderRadius: 4,
  height: 25,
  zIndex: 1,
  "& .MuiChip-label": {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  "& .MuiChip-icon": {
    color: "#fff",
    marginRight: theme.spacing(0.5),
  },
}));

const PlanLabelChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#fff",
  color: "#455354",
  fontWeight: 600,
  borderRadius: 4,
  height: 25,
  marginBottom: theme.spacing(1),
}));

const FeatureLabelChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#f2efe9",
  color: "#455354",
  fontWeight: 600,
  borderRadius: 4,
  height: 25,
  marginBottom: theme.spacing(1),
}));

const MessageCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: 400,
  width: "100%",
  textAlign: "center",
  margin: "0 auto",
  backgroundColor: "#fff",
  borderRadius: 12,
  [theme.breakpoints.down("md")]: {
    maxWidth: "90%",
    padding: theme.spacing(2),
  },
}));

// Styled Dialog components to match your theme
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    backgroundColor: "#f8f8f8",
    borderRadius: 12,
    maxWidth: "600px",
    width: "100%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1),
      maxWidth: "100%",
      width: "calc(100% - 16px)",
    },
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
  padding: theme.spacing(2),
  color: "#f2efe9",
  position: "relative",
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
}));

const DialogBody = styled(DialogContent)(({ theme }) => ({
  backgroundColor: "#fff",
  padding: theme.spacing(3),
}));

const DialogFooter = styled(DialogActions)(({ theme }) => ({
  backgroundColor: "#f2efe9",
  padding: theme.spacing(2),
  justifyContent: "center",
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
}));

const PlanSummary = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: "#f2efe9",
  padding: theme.spacing(2),
  borderRadius: 8,
  marginBottom: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f8f8f8",
    borderRadius: 8,
    "& fieldset": {
      borderColor: "#e1e5e9",
    },
    "&:hover fieldset": {
      borderColor: "#758d8e",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4b5b5d",
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  backgroundColor: "#355142",
  color: "#fff",
  borderRadius: 8,
  padding: theme.spacing(1, 3),
  fontSize: 16,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  "&:hover": {
    backgroundColor: "#283b2e",
  },
  "&.Mui-disabled": {
    backgroundColor: "#aab7c4",
    color: "#fff",
    opacity: 0.7,
  },
  "& .MuiCircularProgress-root": {
    transition: "opacity 0.3s ease-in-out",
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  backgroundColor: "transparent",
  color: "#455354",
  borderRadius: 8,
  padding: theme.spacing(1, 3),
  fontSize: 16,
  "&:hover": {
    backgroundColor: "rgba(69, 83, 84, 0.1)",
  },
}));

interface Plan {
  id: string;
  planName: string;
  price: number;
  duration: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  mode: "public" | "private";
  userId: string | null;
}

// Payment Form Component
const PaymentForm = ({
  selectedPlan,
  onClose,
  setLoading,
}: {
  selectedPlan: Plan;
  onClose: () => void;
  setLoading: (loading: boolean) => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isEmailEditable, setIsEmailEditable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const userEmail = userData?.email || "";

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setLoading(false);
      throw new Error("Card element not found");
    }

    try {
      const { paymentMethod, error: pmError } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            email: email || userEmail,
          },
        });

      if (pmError) {
        throw new Error(pmError.message);
      }

      const result = await dispatch(
        createSubscriptionAction({
          planId: selectedPlan.id,
          paymentMethodId: paymentMethod.id,
          paymentType: paymentMethod.type,
          cardNumber: paymentMethod?.card?.last4 || "",
        }) as any
      );

      if (createSubscriptionAction.fulfilled.match(result)) {
        const { data } = result.payload;
        if (data?.clientSecret) {
          const { error: confirmError } = await stripe.confirmCardPayment(
            data.clientSecret
          );
          if (confirmError) {
            throw new Error(confirmError.message);
          }
        }

        const profileResult = await dispatch(getAuthenticatedUser()).unwrap();

        if (profileResult.success) {
          const profileUser = profileResult.data?.user;
          if (profileUser?.subscription?.id) {
            setSuccess(true);
            setTimeout(() => {
              onClose();
              navigate("/main/dashboard");
            }, 2000);
          } else {
            throw new Error("Subscription not found after creation");
          }
        } else {
          throw new Error(
            profileResult.message || "Failed to fetch user profile"
          );
        }
      } else {
        throw new Error(
          (result.payload as string) || "Subscription creation failed"
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "An error occurred while processing your payment.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <PlanSummary>
        <Box>
          <Typography variant="h6" fontWeight={600} color="#455354">
            {selectedPlan.planName}
          </Typography>
          <Typography variant="body2" color="#666">
            {selectedPlan.description}
          </Typography>
        </Box>
        <Typography variant="h6" fontWeight={700} color="#455354">
          ${selectedPlan.price}/
          {selectedPlan.duration === "monthly" ? "month" : "year"}
        </Typography>
      </PlanSummary>

      <Box sx={{ position: "relative" }}>
        <StyledTextField
          label="Email Address"
          type="email"
          value={email || userEmail}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
          margin="normal"
          disabled={!isEmailEditable && !!userEmail}
        />
        {!!userEmail && (
          <Tooltip title={isEmailEditable ? "Lock Email" : "Edit Email"}>
            <IconButton
              onClick={() => setIsEmailEditable(!isEmailEditable)}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                color: isEmailEditable ? "#4caf50" : "#455354",
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Typography
        variant="subtitle2"
        fontWeight={600}
        mt={2}
        mb={1}
        color="#455354"
      >
        Card Details
      </Typography>

      <Box
        sx={{
          p: 2,
          border: "1px solid #e1e5e9",
          borderRadius: 2,
          backgroundColor: "#f8f8f8",
        }}
      >
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#455354",
                "::placeholder": {
                  color: "#aab7c4",
                },
                backgroundColor: "#f8f8f8",
              },
              invalid: {
                color: "#9e2146",
              },
            },
          }}
        />
      </Box>

      {error && (
        <Typography
          color="error"
          variant="body2"
          sx={{
            mt: 2,
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            bgcolor: "#ffebee",
            padding: "8px 16px",
            borderRadius: 4,
            color: "#d32f2f",
            fontWeight: 600,
            fontSize: 16,
          }}
        >
          {error}
        </Typography>
      )}

      {success && (
        <Typography
          color="success.main"
          variant="body2"
          sx={{
            mt: 2,
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            bgcolor: "#e8f5e9",
            padding: "8px 16px",
            borderRadius: 4,
            color: "#2e7d32",
            fontWeight: 600,
            fontSize: 16,
          }}
        >
          Subscription created successfully! {success ? "🎉" : ""}
        </Typography>
      )}
    </form>
  );
};

const SubscriptionPlan = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    customerPlans: publicPlans,
    loading: planLoading,
    error,
  } = useSelector((state: RootState) => state.planManagement);
  const [selectedDuration, setSelectedDuration] = useState<string>("monthly");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(
      getCustomerUserAssignAndPublicPlan({ page: 1, limit: 100 }) as any
    );
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleBack = () => {
    dispatch(logout());

    // Clear all stored data
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    sessionStorage.clear();

    navigate("/login");
  };

  const handleDurationChange = (duration: string) => {
    setSelectedDuration(duration);
  };

  const filteredPlans = selectedDuration
    ? publicPlans.filter(
        (plan) =>
          plan.duration === selectedDuration || plan.duration === "custom"
      )
    : publicPlans;

  const handleBuyPlan = (plan: Plan) => {
    if (plan.planName.toLowerCase() === "enterprise") {
      navigate("/custom-lead");
    } else {
      setSelectedPlan(plan);
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPlan(null);
  };

  return (
    <Box
      sx={{
        background: "#f9f9f9",
        minHeight: "90vh",
        p: { xs: 2, sm: 3, md: 4 },
        position: "relative",
      }}
    >
      <Tooltip title="Back to Login">
        <StyledBackButton aria-label="back" onClick={handleBack}>
          <ArrowBack />
        </StyledBackButton>
      </Tooltip>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: "#455354", mb: 1 }}
        >
          Plans
        </Typography>
        <Typography variant="body2" color="#666">
          Subscribe to a plan
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            mt: 2,
            maxWidth: "200px",
            mx: "auto",
            p: 0.7,
            background: "#f2efe9",
            borderRadius: 2,
          }}
        >
          <Button
            variant="contained"
            onClick={() => handleDurationChange("monthly")}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              background:
                selectedDuration === "monthly"
                  ? "linear-gradient(to bottom, #758d8e, #4b5b5d)"
                  : "#fff",
              color: selectedDuration === "monthly" ? "#f2efe9" : "#455354",
              "&:hover": {
                background:
                  selectedDuration === "monthly"
                    ? "linear-gradient(to bottom, #758d8e, #4b5b5d)"
                    : "#f5f5f5",
              },
            }}
          >
            Monthly
          </Button>
          <Button
            variant="contained"
            onClick={() => handleDurationChange("yearly")}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              background:
                selectedDuration === "yearly"
                  ? "linear-gradient(to bottom, #758d8e, #4b5b5d)"
                  : "#fff",
              color: selectedDuration === "yearly" ? "#f2efe9" : "#455354",
              "&:hover": {
                background:
                  selectedDuration === "yearly"
                    ? "linear-gradient(to bottom, #758d8e, #4b5b5d)"
                    : "#f5f5f5",
              },
            }}
          >
            Yearly
          </Button>
        </Box>
      </Box>
      {planLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <MessageCard>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: "#455354",
              fontSize: 16,
            }}
          >
            Failed to load plans. Please try again.
          </Typography>
        </MessageCard>
      ) : filteredPlans.length === 0 ? (
        <MessageCard>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: "#455354",
              fontSize: 16,
            }}
          >
            No public plans available for the selected duration.
          </Typography>
        </MessageCard>
      ) : (
        <PerfectScrollbar
          options={{ suppressScrollX: true }}
          style={{
            maxHeight: "calc(100vh - 240px)",
            overflow: "hidden",
          }}
        >
          <PlansContainer>
            {filteredPlans.map((plan) => (
              <Box key={plan.id} sx={{ width: "100%" }}>
                <PlanCard>
                  {plan.isPopular && (
                    <CustomChip
                      label="Popular"
                      icon={<span style={{ fontSize: 14 }}>🔥</span>}
                    />
                  )}
                  <PlanTittleCard>
                    <PlanLabelChip label={plan.planName} />
                    <PlanPrice
                      onClick={
                        plan.planName.toLowerCase() === "enterprise"
                          ? () => handleBuyPlan(plan)
                          : undefined
                      }
                      sx={{
                        cursor:
                          plan.planName.toLowerCase() === "enterprise"
                            ? "pointer"
                            : "default",
                      }}
                    >
                      {plan.planName.toLowerCase() === "enterprise"
                        ? "Custom Pricing"
                        : `$${plan.price}/${
                            plan.duration === "monthly" ? "month" : "year"
                          }`}
                    </PlanPrice>
                  </PlanTittleCard>
                  <Typography
                    color="#666"
                    sx={{ mb: 2, textAlign: "left", mt: 2, fontSize: "0.7rem" }}
                    className="description-text"
                  >
                    {plan.description}
                  </Typography>
                  <Button
                    variant="contained"
                    className="buy-button"
                    onClick={() => handleBuyPlan(plan)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      backgroundColor: "#355142",
                      color: "#fff",
                      width: "100%",
                      borderRadius: 2,
                      padding: theme.spacing(1),
                      fontSize: 12,
                      marginBottom: theme.spacing(1),
                      boxShadow: "0 1px 8px rgba(0,0,0,0.08)",
                      "&:hover": {
                        backgroundColor: "#283b2e",
                      },
                      [theme.breakpoints.down("sm")]: {
                        fontSize: 14,
                        padding: theme.spacing(1),
                      },
                    }}
                  >
                    {plan.planName.toLowerCase() === "enterprise"
                      ? "Contact Us"
                      : "Buy Plan"}
                  </Button>
                  <Divider
                    sx={{
                      margin: "2px 0",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  />
                  <FeaturesList>
                    <FeatureLabelChip label="Feature List" />
                    {plan.features.map((feature, idx) => (
                      <FeatureItem key={idx} className="feature-item">
                        {feature}
                      </FeatureItem>
                    ))}
                  </FeaturesList>
                </PlanCard>
              </Box>
            ))}
          </PlansContainer>
        </PerfectScrollbar>
      )}
      <StyledDialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogHeader>
          <DialogTitle sx={{ color: "#f2efe9", p: 0, m: 0 }}>
            Complete Your Subscription
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#f2efe9",
            }}
          >
            <Close />
          </IconButton>
        </DialogHeader>

        <DialogBody>
          {selectedPlan && (
            <Elements stripe={stripePromise}>
              <PaymentForm
                selectedPlan={selectedPlan}
                onClose={handleCloseDialog}
                setLoading={setLoading}
              />
            </Elements>
          )}
        </DialogBody>

        <DialogFooter>
          <CancelButton onClick={handleCloseDialog}>Cancel</CancelButton>
          <StyledButton
            type="submit"
            form="payment-form"
            variant="contained"
            disabled={loading}
            sx={{ ml: 2, position: "relative" }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={24} sx={{ color: "#fff" }} />
                <Typography variant="button">Processing...</Typography>
              </Box>
            ) : (
              "Subscribe Now"
            )}
          </StyledButton>
        </DialogFooter>
      </StyledDialog>
    </Box>
  );
};

export default SubscriptionPlan;
